import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Package, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

// Fotografía de Pexels (licencia libre, uso comercial sin atribución).
const SHIPPING_IMG =
    "https://images.pexels.com/photos/7857542/pexels-photo-7857542.jpeg?auto=compress&cs=tinysrgb&w=1200";

const OPTIONS = [
    ["Express", "1 a 2 días", "$189", true],
    ["Estándar", "2 a 4 días", "$140", false],
    ["Económico", "3 a 6 días", "$118", false],
];

export default function ShippingFeature() {
    const reduce = useReducedMotion();
    const enter = {
        initial: reduce ? false : { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-80px" },
        transition: { duration: reduce ? 0 : 0.55, ease: "easeOut" },
    };

    return (
        <section
            data-testid="home-shipping"
            className="py-20 sm:py-28 lg:py-32 bg-[#1A1A1A] text-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    {/* Fotografía + cotización encima */}
                    <motion.div {...enter} className="order-2 lg:order-1">
                        <div className="rounded-3xl overflow-hidden">
                            <img
                                src={SHIPPING_IMG}
                                alt="Dos personas etiquetando cajas de pedidos listos para enviar."
                                loading="lazy"
                                decoding="async"
                                width="1200"
                                height="800"
                                className="w-full aspect-[16/10] object-cover"
                            />
                        </div>
                        <div
                            role="img"
                            aria-label="Vista previa de las opciones de envío disponibles al momento de la compra, con tiempos de entrega y precio."
                            className="relative -mt-14 mx-4 sm:mx-10 lg:mx-8 rounded-3xl bg-white text-[#1A1A1A] p-6 sm:p-8 shadow-2xl shadow-black/40"
                        >
                            <div className="flex items-center gap-2 text-sm">
                                <MapPin className="w-4 h-4 text-primary" aria-hidden="true" />
                                <span className="text-zinc-500">Enviar a</span>
                                <span className="font-medium">Mérida, Yucatán</span>
                            </div>

                            <div className="mt-5 space-y-2.5">
                                {OPTIONS.map(([name, eta, price, selected]) => (
                                    <div
                                        key={name}
                                        className={`flex items-center gap-3 rounded-xl border p-3.5 ${
                                            selected
                                                ? "border-primary bg-primary-subtle"
                                                : "border-zinc-200"
                                        }`}
                                    >
                                        <span
                                            className={`w-4 h-4 rounded-full border-2 shrink-0 grid place-items-center ${
                                                selected ? "border-primary" : "border-zinc-300"
                                            }`}
                                        >
                                            {selected && (
                                                <span className="w-2 h-2 rounded-full bg-primary" />
                                            )}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold">{name}</p>
                                            <p className="text-xs text-zinc-500">{eta}</p>
                                        </div>
                                        <span className="ml-auto text-sm font-semibold tabular-nums">
                                            {price}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-5 pt-5 border-t border-zinc-200 flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-primary-subtle grid place-items-center shrink-0">
                                    <Package className="w-4 h-4 text-primary" aria-hidden="true" />
                                </div>
                                <p className="text-xs text-zinc-500 leading-relaxed">
                                    La guía se genera sola cuando confirmas el
                                    pedido. Solo imprímela y entrega el paquete.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Texto */}
                    <motion.div
                        {...enter}
                        transition={{ ...enter.transition, delay: reduce ? 0 : 0.12 }}
                        className="order-1 lg:order-2"
                    >
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300">
                            Envíos
                        </p>
                        <h2 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-[-0.03em] leading-[1.02] text-balance">
                            Envía a todo México sin pelearte con la paquetería
                        </h2>
                        <p className="mt-5 text-lg text-zinc-300 leading-relaxed">
                            Tu cliente elige el envío al momento de comprar y
                            paga la tarifa exacta. Tú generas la guía con un
                            clic y descargas el PDF listo para pegar.
                        </p>
                        <ul className="mt-8 space-y-3 text-[15px] text-zinc-300">
                            {[
                                "Cotización automática según el peso y el destino.",
                                "Guía en PDF y número de rastreo desde tu panel.",
                                "El estado del envío se actualiza solo, del almacén a la entrega.",
                            ].map((t) => (
                                <li key={t} className="flex gap-3">
                                    <span
                                        aria-hidden="true"
                                        className="w-1.5 h-1.5 rounded-full bg-indigo-300 shrink-0 mt-2.5"
                                    />
                                    {t}
                                </li>
                            ))}
                        </ul>
                        <Button
                            asChild
                            className="mt-9 h-12 px-7 rounded-full text-[15px] font-semibold bg-white text-[#1A1A1A] hover:bg-zinc-100"
                        >
                            <Link to="/registro?rol=merchant">
                                Crear mi tienda gratis
                                <ArrowRight className="w-4 h-4 ml-1.5" aria-hidden="true" />
                            </Link>
                        </Button>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
